import {makeAutoObservable, toJS} from "mobx";
import type ProfileStore from "../ProfileStore";
import type {MithraMaterializedApi} from "~/services/MithraMaterializedApi.ts";
import type AuthStore from "../../authentication/AuthStore";
import type {BagStore} from "../BagStore";
import {
    CategorizationReviewPageController
} from "~/pages/categorization/controllers/CategorizationReviewPageController.ts";
import {AiCategorizationJobDelegate} from "./AiCategorizationJobDelegate";
import {TaxonomyCategoryProvider} from "../TaxonomyCategoryProvider";
import type {ApprovalStore} from "../ApprovalStore";
import {
    CategorizationStatisticsDashboardController
} from "~/pages/categorization/components/statistics/CategorizationStatisticsDashboardController.ts";
import {CategorizationStatusDelegate} from "./CategorizationStatusDelegate";
import {environment} from "~/env.ts";
import {isUnderConstructionMode} from "~/utils/underConstructionMode.ts";
import {
    PartHistoryDialogStore
} from "~/pages/categorization/components/review-table/review-table-muix/part-history/PartHistoryDialogStore.ts";

/**
 * CategorizationReviewStore:
 * - Starting point for initialization and loading
 * - Exposes simple KPI's that can be used throughout the application
 */
export class CategorizationReviewStore {
    readonly aiJobDelegate: AiCategorizationJobDelegate;
    readonly categories: TaxonomyCategoryProvider;
    readonly reviewPageController: CategorizationReviewPageController;
    readonly statisticsDashboardController: CategorizationStatisticsDashboardController;
    // We can't initialize this here, somehow it doesn't work with reactions
    readonly statusDelegate: CategorizationStatusDelegate;
    readonly partHistoryDialogStore: PartHistoryDialogStore;

    _desiredBagId: number | null = null
    _desiredTaxonomyId: number | null = null
    _desiredTaxonomySize: number | null = null
    isValidationBag = false;

    constructor(
        private approvalStore: ApprovalStore,
        private auth: AuthStore,
        private bagStore: BagStore,
        private api: MithraMaterializedApi,
        private profile: ProfileStore,
    ) {
        makeAutoObservable(this);
        this.aiJobDelegate = new AiCategorizationJobDelegate(this.profile, this.api, this)
        this.categories = new TaxonomyCategoryProvider(this.profile, this.api);
        this.reviewPageController = new CategorizationReviewPageController(this, this.approvalStore, this.api, this.auth, this.profile, this.bagStore)
        this.statisticsDashboardController = new CategorizationStatisticsDashboardController(this.profile, this.api);
        this.statusDelegate = new CategorizationStatusDelegate(this.api, this, this.auth);
        this.partHistoryDialogStore = new PartHistoryDialogStore(this.api);

        // Setup links that have dependencies in both directions
        this.approvalStore.categorizationApproval.categorizationStore = this;

        // // TODO: Consider moving to reviewPageController?
        // reaction(() => [this.bagId, this.taxonomySize] as const, ([bagId, taxonomySize]) => {
        //     console.log('CategorizationStore Triggering change of bagId', bagId);
        //     if (bagId && taxonomySize) {
        //         this.reviewPageController.setRequestBagId(bagId)
        //         this.reInitialize(bagId, taxonomySize)
        //     }
        // });
    }

    get bagId(): number {
        return this._desiredBagId || -1;
    }

    /**
     * This denotes how deep we can navigate in the taxonomy and how deep we load in the statistics
     *
     * Initially it must be set, so we pick 3, but later this must will updated when the bag is known
     * TODO[integration]: Move max level to profile store or get it from the bag
     */
    get taxonomySize(): number {
        return this._desiredTaxonomySize || -1;
    }

    init(bagId: number, taxonomyId: number | null, taxonomySize: number, asValidationBag = false) {
        console.log('CategorizationStore.CategorizationReviewPageController.reInitialize', toJS(({
            bagId,
            taxonomySize,
            asValidationBag
        })));

        if (!taxonomyId) {
            const errData = {bag: bagId, taxonomy: taxonomyId};
            console.error('Cannot initialize review results as the current taxonomy id is missing', errData)

            taxonomyId = null;
            this.categories.arrest('No taxonomy exists')
        } else {
            this.categories.fillCategoriesFromTaxonomy(taxonomyId)
        }


        this.reviewPageController.reviewPageFilterController.clearFilters()
        this._desiredBagId = bagId || null;
        this._desiredTaxonomySize = taxonomySize;
        this._desiredTaxonomyId = taxonomyId;

        if (asValidationBag && !this.profile.p.gotoBuFilterInSample) {
            // Skip the welcome page
            this.reviewPageController.navigateToPage('review');
        }

        this.isValidationBag = asValidationBag;
        this.reviewPageController.reviewPageStatisticsController.reset()

        const bu = this.reviewPageController.reviewPageFilterController.selectedBusinessUnitId;
        this.reviewPageController.reviewPageStatisticsController.request(this.bagId, bu, taxonomySize)
    }

    refresh() {
        if (this._desiredTaxonomyId) {
            this.categories.fillCategoriesFromTaxonomy(this._desiredTaxonomyId)
        }
    }

    get summaryResultKeyValues() {
        // Helper function to expose the same key results everywhere
        return this.reviewPageController.reviewPageStatisticsController.summaryResultKeyValues;
    }

    /**
     * Default is false
     */
    viewOnlyState = (environment.isTestReviewPage === 'view-only');

    get viewOnly() {
        return !this.auth.hasEditPermission
            || isUnderConstructionMode(this.auth)
            || this.viewOnlyState
    }

    setViewOnly(viewOnly: boolean) {
        this.viewOnlyState = viewOnly;
    }

    ensureViewOnlyIsSet(viewOnly: boolean) {
        if (viewOnly && !this.viewOnlyState) {
            this.viewOnlyState = true;
        } else if (!viewOnly && this.viewOnlyState) {
            this.viewOnlyState = false;
        }
    }

    /**
     * Toggle between the read-only and edit mode of the categorization page
     */
    get canEditReviewPage(): boolean {
        return !this.viewOnly
        // TODO: Test with missing or SLOW this.categories.taxonomy_categories

            // Note: We do allow reviews to be made when the send for approval is being created.
            // These will be queued so the user can continue working. Only the loading will take a bit longer
            && !this.reviewPageController.reviewPageApprovalController.isSendForApprovalWaiting

            // We cannot review if there are not taxonomy_categories loaded in (yet)
            && Boolean(this.categories.taxonomy_categories)
    }
}
